/**
 * IntelligenceDashboardCockpit — Cockpit-language frame for /intelligence.
 *
 * Refactored to a focused risk + governance surface:
 *   - Operational Risk
 *   - Override Intelligence
 *   - Pipeline Intelligence (link card only — no embed)
 *
 * Revenue Leakage → /revenue-intelligence (canonical)
 * Sales Performance → /command-dashboard (Sales Funnel Breakdown)
 * Lead Sources → /marketing-attribution (canonical)
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  CockpitPage, CockpitMasthead, HeroMetric, HeroDivider,
  CockpitKpiStrip, CockpitTabs, CockpitSection,
} from "@/components/cockpit/CockpitShell";
import { Card, CardContent } from "@/components/ui/card";
import { Activity, ArrowRight } from "lucide-react";
import { useAppData } from "@/data/AppDataContext";
import { OperationalRisk, OverrideIntelligence } from "./IntelligenceDashboard";

const fmt$ = (n: number) => `$${((n) / 1000).toLocaleString(undefined, {maximumFractionDigits: 0})}K`;

function PipelineIntelligenceLinkCard() {
  return (
    <Card className="border-dashed hover:border-primary/40 transition-colors">
      <CardContent className="p-4 flex items-center gap-4">
        <div className="rounded-lg bg-primary/10 p-2.5">
          <Activity className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground">Pipeline Intelligence</p>
          <p className="text-xs text-muted-foreground">Forecasting & funnel scoring (Beta)</p>
        </div>
        <Link
          to="/pipeline-intelligence"
          className="text-xs font-medium px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors flex items-center gap-1.5"
        >
          Open Pipeline Intelligence <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </CardContent>
    </Card>
  );
}

export default function IntelligenceDashboardCockpit() {
  const { jobs, salesOpportunities, issues } = useAppData();
  const [tab, setTab] = useState("ops");

  const kpis = useMemo(() => {
    const totalRevenue = jobs.reduce((s, j) => s + (j.financials?.contractValue ?? 0), 0);
    const won = salesOpportunities.filter((o) => o.stage === "Closed Won").length;
    const lost = salesOpportunities.filter((o) => o.stage === "Closed Lost").length;
    const closeRate = won + lost > 0 ? Math.round((won / (won + lost)) * 100) : 0;
    const openIssues = issues.filter((i) => i.status !== "Resolved" && i.status !== "Closed").length;
    const urgentIssues = issues.filter(
      (i) => i.severity === "Urgent" && i.status !== "Resolved" && i.status !== "Closed",
    ).length;
    return { totalRevenue, won, lost, closeRate, openIssues, urgentIssues, jobs: jobs.length };
  }, [jobs, salesOpportunities, issues]);

  const sectionMeta: Record<string, { eyebrow: string; title: string }> = {
    ops:       { eyebrow: "Execution Risk", title: "Operational Risk" },
    overrides: { eyebrow: "Governance",     title: "Override Intelligence" },
  };
  const meta = sectionMeta[tab];

  return (
    <CockpitPage>
      <CockpitMasthead
        // eyebrow="Reporting · Intelligence"Workspace · Overview
        title="Intelligence Dashboard"
        right={
          <>
            <HeroMetric label="Pipeline Value" value={fmt$(kpis.totalRevenue)} hero />
            <HeroDivider />
            <HeroMetric
              label="Close Rate"
              value={`${kpis.closeRate}%`}
              tone={kpis.closeRate >= 30 ? "good" : "warn"}
            />
            <HeroDivider />
            <HeroMetric
              label="Open Issues"
              value={kpis.openIssues}
              tone={kpis.openIssues > 5 ? "warn" : "good"}
            />
          </>
        }
      />

      <CockpitKpiStrip
        items={[
          { label: "Total Jobs", value: kpis.jobs },
          { label: "Wins", value: kpis.won, tone: "good" },
          { label: "Losses", value: kpis.lost, tone: kpis.lost > kpis.won ? "warn" : "default" },
          { label: "Urgent Issues", value: kpis.urgentIssues, tone: kpis.urgentIssues > 0 ? "bad" : "good" },
        ]}
      />

      <PipelineIntelligenceLinkCard />

      <CockpitTabs
        value={tab}
        onChange={setTab}
        tabs={[
          { value: "ops",       label: "Operational Risk" },
          { value: "overrides", label: "Override Intelligence" },
        ]}
      />

      <CockpitSection eyebrow={meta.eyebrow} title={meta.title}>
        <div className="cockpit-surface">
          {tab === "ops"       && <OperationalRisk />}
          {tab === "overrides" && <OverrideIntelligence />}
        </div>
      </CockpitSection>
    </CockpitPage>
  );
}
